// Color palette
export const colors = {
  primary: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
  },
  gray: {
    50: '#fafafa',
    100: '#f4f4f5',
    200: '#e4e4e7',
    300: '#d4d4d8',
    400: '#a1a1aa',
    500: '#71717a',
    600: '#52525b',
    700: '#3f3f46',
    800: '#27272a',
    900: '#18181b',
  },
  success: {
    50: '#ecfdf5',
    500: '#10b981',
    600: '#059669',
  },
  error: {
    50: '#fef2f2',
    500: '#ef4444',
    600: '#dc2626',
  },
  warning: {
    50: '#fffbeb',
    500: '#f59e0b',
    600: '#d97706',
  },
  background: {
    page: '#ffffff',
    card: '#ffffff',
    subtle: '#fafafa',
    hover: '#f4f4f5',
  }
};

// Typography scale
export const typography = {
  h1: {
    fontSize: 'clamp(2.5rem, 5vw, 3.75rem)',
    lineHeight: '1.1',
    fontWeight: '800',
    letterSpacing: '-0.03em',
  },
  h2: {
    fontSize: 'clamp(2rem, 4vw, 2.75rem)',
    lineHeight: '1.2',
    fontWeight: '700',
    letterSpacing: '-0.02em',
  },
  h3: {
    fontSize: 'clamp(1.5rem, 3vw, 1.875rem)',
    lineHeight: '1.3',
    fontWeight: '600',
    letterSpacing: '-0.01em',
  },
  h4: {
    fontSize: '1.25rem',
    lineHeight: '1.4',
    fontWeight: '600',
  },
  body1: {
    fontSize: '1rem',
    lineHeight: '1.6',
    fontWeight: '400',
  },
  body2: {
    fontSize: '0.9375rem',
    lineHeight: '1.6',
    fontWeight: '400',
  },
  caption: {
    fontSize: '0.8125rem',
    lineHeight: '1.5',
    fontWeight: '400',
  },
  button: {
    fontSize: '0.9375rem',
    lineHeight: '1.5',
    fontWeight: '500',
    letterSpacing: '0.01em',
  }
};

// Spacing scale
export const spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
};

// Shadows
export const shadows = {
  sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
};

// Border radiuses
export const radii = {
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  '2xl': '1.5rem',
  full: '9999px',
};

// Layout
export const layout = {
  header: {
    height: '64px',
  },
  maxWidth: '1200px',
  containerPadding: spacing[6],
};

// Transitions
export const transitions = {
  base: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  smooth: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  bounce: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  spring: 'all 500ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
};

// Common component styles
export const components = {
  card: {
    backgroundColor: colors.background.card,
    borderRadius: radii.lg,
    border: `1px solid ${colors.gray[200]}`,
    padding: spacing[6],
    transition: transitions.smooth,
    '&:hover': {
      boxShadow: shadows.md,
    },
  },
  button: {
    base: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing[2],
      padding: `${spacing[3]} ${spacing[5]}`,
      borderRadius: radii.lg,
      border: 'none',
      cursor: 'pointer',
      transition: transitions.bounce,
      fontWeight: '500',
      '&:focus': {
        outline: 'none',
        boxShadow: `0 0 0 3px ${colors.primary[100]}`,
      },
    },
    primary: {
      backgroundColor: colors.primary[600],
      color: '#ffffff',
      '&:hover': {
        backgroundColor: colors.primary[700],
        transform: 'translateY(-2px)',
        boxShadow: shadows.md,
      },
      '&:active': {
        backgroundColor: colors.primary[800],
        transform: 'translateY(0)',
      },
    },
    secondary: {
      backgroundColor: colors.gray[100],
      color: colors.gray[900],
      '&:hover': {
        backgroundColor: colors.gray[200],
        transform: 'translateY(-2px)',
        boxShadow: shadows.sm,
      },
      '&:active': {
        backgroundColor: colors.gray[300],
        transform: 'translateY(0)',
      },
    },
    success: {
      backgroundColor: colors.success[500],
      color: '#ffffff',
      '&:hover': {
        backgroundColor: colors.success[600],
        transform: 'translateY(-2px)',
        boxShadow: shadows.md,
      },
    },
    disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      '&:hover': {
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
  input: {
    base: {
      width: '100%',
      padding: `${spacing[3]} ${spacing[4]}`,
      borderRadius: radii.lg,
      border: `2px solid ${colors.gray[200]}`,
      backgroundColor: colors.background.card,
      transition: transitions.smooth,
      ...typography.body1,
      '&:focus': {
        outline: 'none',
        borderColor: colors.primary[400],
        boxShadow: `0 0 0 3px ${colors.primary[100]}`,
      },
      '&:disabled': {
        backgroundColor: colors.gray[50],
        cursor: 'not-allowed',
      },
    },
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: `0 ${spacing[6]}`,
  },
}; 